import React from 'react';
import {
  AWSActionType,
  RdsAction,
  Ec2Action,
  AsgAction,
  AWSResourceType,
} from './types';
import { ActionConfirmationDisplay } from '../../types';
import { ActionConfirmationDialog } from '../../ActionConfirmationDialog';

interface ConfirmationDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  action: AWSActionType;
  isLoading: boolean;
  service: AWSResourceType;
}

interface DialogConfig {
  title: string;
  content: string;
  actionDisplay: ActionConfirmationDisplay;
}

const getDialogConfig = (
  action: AWSActionType,
  service: AWSResourceType,
): DialogConfig => {
  switch (service) {
    case 'rds': {
      const actionDisplays: Record<RdsAction, ActionConfirmationDisplay> = {
        start: { text: 'Start', color: 'primary' },
        stop: { text: 'Stop', color: 'secondary' },
        restart: { text: 'Restart', color: 'primary' },
      };
      const actionDisplay = actionDisplays[action as RdsAction];
      const title = `Confirm ${actionDisplay.text} Database`;
      const content = `Are you sure you want to ${actionDisplay.text.toLowerCase()} databases?`;
      return { title, content, actionDisplay };
    }
    case 'ec2': {
      const actionDisplays: Record<Ec2Action, ActionConfirmationDisplay> = {
        start: { text: 'Start', color: 'primary' },
        stop: { text: 'Stop', color: 'secondary' },
        restart: { text: 'Restart', color: 'primary' },
      };

      const actionDisplay = actionDisplays[action as Ec2Action];
      const title = `Confirm ${actionDisplay.text} Virtual Machine`;
      const content = `Are you sure you want to ${actionDisplay.text.toLowerCase()} virtual machines?`;
      return { title, content, actionDisplay };
    }
    case 'asg': {
      const actionDisplays: Record<AsgAction, ActionConfirmationDisplay> = {
        start: { text: 'Start', color: 'primary' },
        stop: { text: 'Stop', color: 'secondary' },
        restart: { text: 'Restart', color: 'primary' },
      };
      const actionDisplay = actionDisplays[action as AsgAction];
      const title = `Confirm ${actionDisplay.text} Auto Scaling groups?`;
      const content = `Are you sure you want to ${actionDisplay.text.toLowerCase()} Auto Scaling groups?`;
      return { title, content, actionDisplay };
    }
    default: {
      return {
        title: 'Confirm',
        content: `Are you sure you want to execute ${action} action?`,
        actionDisplay: { text: action, color: 'primary' },
      };
    }
  }
};

export const AwsActionConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  open,
  onClose,
  onConfirm,
  action,
  isLoading,
  service,
}) => {
  if (!action) {
    return null;
  }

  const { title, content, actionDisplay } = getDialogConfig(action, service);

  return (
    <ActionConfirmationDialog<AWSActionType>
      open={open}
      onClose={onClose}
      onConfirm={onConfirm}
      action={action}
      isLoading={isLoading}
      actionDisplay={actionDisplay}
      title={title}
      content={content}
    />
  );
};
